// ═════════════════════════════════════════════════════════════════════════════
//  CLOCK CORRECTION — the punch rows an Undo, a day-sheet fix or a Move writes
// ═════════════════════════════════════════════════════════════════════════════
// Pure: no DOM, no Supabase. Punches are append-only, so nothing here edits a row: a correction is a NEW row that
// points at the one it replaces (replaces_id), a void is a new row that cancels one. Every builder returns INSERT
// payloads, in insert order; the caller sends them (online, or through the outbox). Never sent_at / punched_by /
// org_id: the stamp trigger sets those (contract §3.1).
//
// ctx = { workerId, projectId, workDate: 'YYYY-MM-DD', punches, settings? }: the punches are the EFFECTIVE punches of
// that worker on that project on that work_date, any order. They go through effectivePunches again here, so a voided
// or replaced row that slips in still does not count. settings (optional, the settingsFrom(org) of the company) is
// read for breakMinutes only, to chain the day's shifts the way the break rule does; left out, the BC default
// counts. daySheetPayloads / daySheetLines are the ONE builder of the field and the admin day sheet: the sheets only
// read the DOM and insert.
//
// A correction (source 'correction') skips the sequence guard of the database but not its CHECK: work_date within one
// day of the punch's UTC date. A void copies its target's punched_at and work_date, so it always passes; an added or
// fixed punch keeps the DAY's work_date (ctx.workDate), so the out of a night shift on the next calendar day stays on
// the day of its in.
//
// Errors are short CODES in the message, for the sheets to word (contract §4.6): RangeError 'out before in', 'shift
// open', 'break order', 'negative break', 'stale target', 'date too far'; and TypeError 'reason', the only TypeError.

import { effectivePunches, dayFromPunches, fmtTime, DEFAULT_SETTINGS } from './time-clock.js';
import { localDateStr } from './period.js';

export { localDateStr as localDate } from './period.js';   // 'YYYY-MM-DD' of an instant in the device's zone

export const DEFAULT_OUT_REASON = 'forgot to check out';

// The words the day sheets give those codes: ONE table for the field's sheet and the admin's (contract §4.6), code ->
// the i18n key (i18n/en.json) whose sentence both show. They used to word the codes each in its own table and drifted:
// the admin's 'reason' read a field label. A new code needs a row here (a unit test scans this file's throws).
export const FIX_ERROR_KEYS = {
  'out before in': 'tc.errOutBeforeIn',
  'shift open': 'tc.errShiftOpen',
  'break order': 'tc.errBreakOrder',
  'negative break': 'tc.errNegativeBreak',
  'stale target': 'tc.errStaleTarget',
  'date too far': 'tc.errDateTooFar',
  reason: 'tc.errReason',
};
// The key of an error the builders threw, or null for anything else (an own property only: 'toString' is no code).
export const fixErrorKey = e => (Object.prototype.hasOwnProperty.call(FIX_ERROR_KEYS, e?.message) ? FIX_ERROR_KEYS[e.message] : null);

const ms = ts => new Date(ts ?? NaN).getTime();         // ISO text, Date or ms -> an instant; missing -> NaN (new Date(null) is 1970)
const clean = reason => String(reason ?? '').trim();    // the database trims too: blank is no reason

// One INSERT payload. A correction, an Undo or a Move carries no fix of its own: the caller adds the GPS, if any.
const punch = (project_id, worker_id, kind, punched_at, work_date, source, replaces_id = null, reason = null) => ({
  id: crypto.randomUUID(), project_id, worker_id, kind, punched_at, work_date, source, replaces_id, reason,
  lat: null, lng: null, gps_accuracy_m: null,
});

// The row `target` is paired with, read the way dayFromPunches reads a day: an out with the in it closes, an in with
// the out that closes it, a break_end with its break_start and back (an in while a shift is open, and a break_start
// inside a break, open nothing). null when it has no mate, or is not on the day.
function mateOf(eff, target) {
  let shift = null, brk = null;   // the open shift's in / the open break_start
  for (const p of eff) {
    if (p.kind === 'in') shift ??= p;
    else if (p.kind === 'break_start') { if (shift && !brk) brk = p; }
    else if (p.kind === 'break_end') {
      if (p.id === target.id) return brk;
      if (brk?.id === target.id) return p;
      brk = null;
    } else if (p.kind === 'out') {
      if (p.id === target.id) return shift;
      if (shift?.id === target.id) return p;
      shift = brk = null;
    }
  }
  return null;
}

// The table CHECK time_punches_work_date_near, so a slip of the date box stops here and not at the database (23514):
// the instant must be within one day of the work_date by the UTC date (the CHECK) and by the local date (the
// calendar the sheet is typed on). An unreadable instant or work_date is not near.
function checkNear(ctx, atIso) {
  const t = ms(atIso), day = ymd => Date.parse(ymd) / 86400000;
  const near = ymd => Math.abs(day(ymd) - day(ctx.workDate)) <= 1;
  if (!(Number.isFinite(t) && near(localDateStr(t)) && near(new Date(t).toISOString().slice(0, 10)))) throw new RangeError('date too far');
}

// ── Day sheet: add a check-out, fix a time, set the break ───────────────────

// The missing check-out of a day, at the instant the caller picked.
export function correctionAddOut(ctx, atIso, reason) {
  checkNear(ctx, atIso);
  // An out before the day's last in sorts ahead of it: a dup_out, and that in stays open.
  if (effectivePunches(ctx.punches).some(p => p.kind === 'in' && ms(p.punched_at) > ms(atIso))) throw new RangeError('out before in');
  return [punch(ctx.projectId, ctx.workerId, 'out', atIso, ctx.workDate, 'correction', null, clean(reason) || DEFAULT_OUT_REASON)];
}

// One punch moved to another instant: a new row of the same kind that replaces `target`.
export function correctionFixTime(ctx, target, atIso, reason) {
  const why = clean(reason);
  if (!why) throw new TypeError('reason');
  checkNear(ctx, atIso);
  // A pair may not cross its own mate: an out moved before the in it closes sorts ahead of it and leaves that in
  // open (an in moved past its out the same), and a break_end before its break_start is a break that ends before it began.
  const mate = mateOf(effectivePunches(ctx.punches), target);
  if (mate) {
    const first = target.kind === 'in' || target.kind === 'break_start';   // the first of its pair may not pass its mate, the second may not go before it
    if (first ? ms(atIso) > ms(mate.punched_at) : ms(atIso) < ms(mate.punched_at)) throw new RangeError(target.kind.startsWith('break') ? 'break order' : 'out before in');
  }
  return [punch(ctx.projectId, ctx.workerId, target.kind, atIso, ctx.workDate, 'correction', target.id, why)];
}

// The day's break set to `minutes`: a void of every break row it has, then ONE pair centred on a closed shift
// (minutes 0 is a pair at one instant: it wins over the automatic deduction, so "no lunch" can be recorded).
// Which shift: the engine ignores a break_start outside a shift, and its rule judges only the LAST block of the day,
// the shifts chained while the gap from one's out to the next one's in is under breakMinutes (applyBreakRule: an
// earlier block followed by a rest gap is "rested", never deducted or flagged). So the pair goes in the longest
// closed shift of that last block: a pair in the rested block would leave the judged one without any, and the rule
// would deduct on top of what was typed.
// ponytail: ONE value per job-day goes to its last block. A split shift whose blocks are both due (the gap between
// them breakAfterHours or more) keeps the earlier block's own rule deduction. Upgrade: a value per block. Also no cap
// on the minutes: a break longer than its shift sits partly outside it, where the engine does not count it.
export function correctionSetBreak(ctx, minutes, reason) {
  if (!Number.isFinite(minutes) || minutes < 0) throw new RangeError('negative break');
  const why = clean(reason);
  if (!why) throw new TypeError('reason');
  const eff = effectivePunches(ctx.punches);
  const day = dayFromPunches(eff, DEFAULT_SETTINGS);   // only its shifts' instants are read: the break rule moves no in or out
  const closed = day ? day.shifts.filter(s => !s.open) : [];
  if (!closed.length || day.open) throw new RangeError('shift open');
  const gapMs = (ctx.settings?.breakMinutes ?? DEFAULT_SETTINGS.breakMinutes) * 60000;
  let block = [], endMs = -Infinity;   // the block being built; the latest out so far
  for (const s of closed) {
    if (ms(s.startAt) - endMs >= gapMs) block = [];   // applyBreakRule's own comparison: a gap of breakMinutes or more ends the block
    block.push(s);
    endMs = Math.max(endMs, ms(s.endAt));
  }
  const len = s => ms(s.endAt) - ms(s.startAt);
  const longest = block.reduce((a, s) => len(s) > len(a) ? s : a);   // a tie: the earlier shift
  const start = Math.floor((ms(longest.startAt) + ms(longest.endAt)) / 2) - minutes * 30000;
  const iso = t => new Date(t).toISOString();
  return [
    ...eff.filter(p => p.kind === 'break_start' || p.kind === 'break_end')
      .map(p => punch(ctx.projectId, ctx.workerId, 'void', p.punched_at, p.work_date, 'correction', p.id, why)),
    punch(ctx.projectId, ctx.workerId, 'break_start', iso(start), ctx.workDate, 'correction', null, why),
    punch(ctx.projectId, ctx.workerId, 'break_end', iso(start + minutes * 60000), ctx.workDate, 'correction', null, why),
  ];
}

// The sheet's date and time boxes ('2026-10-19', '15:30') are local wall-clock time. -> ISO; null while either box is
// empty or unreadable.
function atLocal(date, time) {
  const [y, mo, d] = String(date ?? '').split('-').map(Number);
  const [h, mi] = String(time ?? '').split(':').map(Number);
  const at = new Date(y, mo - 1, d, h, mi);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

// What a Save on the day sheet writes, in insert order: the added check-out ('addOut') or the fixed punch ('fix', a
// picked row AND a typed time), then the break rows when Break (min) changed. Nothing is written without an instant;
// nothing to write is [] (the sheet says so and stays open). The reason is required for a fix, for a break change,
// and for every save when reasonRequired (the admin's sheet); an added check-out alone takes DEFAULT_OUT_REASON. A box
// read as text ('30') or left empty (that is not 0) is no change. The builders' RangeErrors pass through, unchanged.
// The break is built on the day as THIS save leaves it: the check-out added now closes the shift first, a fixed time
// moves the shift the pair must sit in, and a fixed break row is voided by its own correction (not twice by the row
// it replaced). 'shift open' then means the day is still open after the save.
export function daySheetPayloads(ctx, { mode, targetId, date, time, breakMin, breakMinWas, reason, reasonRequired }) {
  const at = atLocal(date, time);
  let target = null;
  if (mode === 'fix' && targetId) {
    target = effectivePunches(ctx.punches).find(p => p.id === targetId);
    if (!target) throw new RangeError('stale target');   // the list went stale under the radio buttons
  }
  const addOut = mode === 'addOut' && at !== null, fix = target !== null && at !== null;
  const minutesOf = v => v === '' || v == null ? null : Number(v);
  const minutes = minutesOf(breakMin);
  const breakChanged = minutes !== null && minutes !== minutesOf(breakMinWas);
  const why = clean(reason);
  if (!why && (reasonRequired || fix || breakChanged)) throw new TypeError('reason');
  const main = [
    ...(addOut ? correctionAddOut(ctx, at, why) : []),
    ...(fix ? correctionFixTime(ctx, target, at, why) : []),
  ];
  return breakChanged ? [...main, ...correctionSetBreak({ ...ctx, punches: [...ctx.punches, ...main] }, minutes, why)] : main;
}

// The sheet's list: the effective punches in time order, one entry per radio row.
export function daySheetLines(punches) {
  return effectivePunches(punches).map(p => ({
    id: p.id, kind: p.kind, time: fmtTime(p.punched_at), by: p.punched_by_name,
    corrected: p.source === 'correction', reason: p.reason,
  }));
}

// ── Worker card: Undo and Move ──────────────────────────────────────────────

// Cancels the worker's own last punch: a void with the target's own instant and work_date.
export const undoPunch = target =>
  [punch(target.project_id, target.worker_id, 'void', target.punched_at, target.work_date, 'self', target.id)];

// The punch an Undo may cancel: the last effective one, and only when the user made it (a crew punch is the
// foreman's). A void and its target drop out together, so after an Undo still in the outbox nothing is offered.
// After a Move (out A + in B at one instant) the last row is the out of A, on purpose: undoing one half would leave
// the worker on no job or on two (the card offers "Undo check-in" only when this is an in and the card is checked in).
export function undoTarget(ownPunches, uid) {
  const eff = effectivePunches(ownPunches), last = eff[eff.length - 1];
  return uid && last?.punched_by === uid ? last : null;
}

// "Move to X?": out of the job he is on and in on the new one, one instant. The in takes the local day of that
// instant; the out the day of the shift it closes (ctx.workDate, the open in's work_date, as the database stamps
// it): a night shift moved after midnight stays one day on the phone too. No ctx.workDate: the instant's day.
export function movePair(ctx, toProjectId, atIso, source) {
  const day = localDateStr(atIso);
  return [
    punch(ctx.projectId, ctx.workerId, 'out', atIso, ctx.workDate || day, source),
    punch(toProjectId, ctx.workerId, 'in', atIso, day, source),
  ];
}

// ── Open shifts ─────────────────────────────────────────────────────────────

// THE rule for "today's open shift" against "an older open day", shared by the worker card, the crew card and the
// admin's On site now. The open in is current when it is of today's local date, or younger than longShiftHours.
// Time-based on purpose: clock_context().open carries no work_date, and a calendar test would take Check out away from
// the night shift (in Mon 22:00, 2 h old at 00:01 Tuesday). What is neither is the "forgot to check out" case.
// Unreadable or missing `sinceIso` is not current.
export function isCurrentOpen(sinceIso, settings, now = Date.now()) {
  return localDateStr(sinceIso) === localDateStr(now) || ms(now) - ms(sinceIso) <= settings.longShiftHours * 3600000;
}
